export const RECRUITMENT_CHART_FILTER_BY_COST_CENTER = 1;
export const RECRUITMENT_CHART_FILTER_BY_JOB_TITLE_GROUP = 2;
export const RECRUITMENT_CHART_FILTER_BY_MONTH = 3;
export const RECRUITMENT_CHART_FILTER_BY_STAGE = 4;
export const RECRUITMENT_CHART_FILTER_BY_AREA = 5;
export const RECRUITMENT_CHART_FILTER_BY_PURPOSE = 6;

export type RecruitmentMetricsChartFilter =
  | typeof RECRUITMENT_CHART_FILTER_BY_COST_CENTER
  | typeof RECRUITMENT_CHART_FILTER_BY_JOB_TITLE_GROUP
  | typeof RECRUITMENT_CHART_FILTER_BY_MONTH
  | typeof RECRUITMENT_CHART_FILTER_BY_STAGE
  | typeof RECRUITMENT_CHART_FILTER_BY_AREA
  | typeof RECRUITMENT_CHART_FILTER_BY_PURPOSE;

export type RecruitmentMetricsParams = {
  companyId?: string | null;
  dateBegin?: string;
  dateEnd?: string;
  chartFilter?: RecruitmentMetricsChartFilter[];
  costCenterId?: string[];
  jobTitleGroupId?: string[];
  yearMonth?: string[];
  stageId?: string[];
  areaType?: string[];
  /** RecruitmentVacancyPurpose.NOT_INFORMED_ID ou ids do enum */
  purpose?: number[];
};

export class RecruitmentByCostCenterItem {
  public costCenterId: string = '';
  public costCenterName: string = '';
  public count: number = 0;

  static create(input: unknown): RecruitmentByCostCenterItem {
    const item = new RecruitmentByCostCenterItem();
    const payload = isRecord(input) ? input : {};

    item.costCenterId = String(payload.costCenterId ?? '');
    item.costCenterName = String(payload.costCenterName ?? '');
    item.count = Number(payload.count ?? 0) || 0;

    return item;
  }
}

export class RecruitmentByJobTitleGroupItem {
  public jobTitleGroupId: string = '';
  public jobTitleGroupName: string = '';
  public count: number = 0;

  static create(input: unknown): RecruitmentByJobTitleGroupItem {
    const item = new RecruitmentByJobTitleGroupItem();
    const payload = isRecord(input) ? input : {};

    item.jobTitleGroupId = String(payload.jobTitleGroupId ?? '');
    item.jobTitleGroupName = String(payload.jobTitleGroupName ?? '');
    item.count = Number(payload.count ?? 0) || 0;

    return item;
  }
}

export class RecruitmentByMonthItem {
  public year: number = 0;
  public month: number = 0;
  public openedCount: number = 0;
  public closedCount: number = 0;

  static create(input: unknown): RecruitmentByMonthItem {
    const item = new RecruitmentByMonthItem();
    const payload = isRecord(input) ? input : {};

    item.year = Number(payload.year ?? 0) || 0;
    item.month = Number(payload.month ?? 0) || 0;
    item.openedCount = Number(payload.openedCount ?? 0) || 0;
    item.closedCount = Number(payload.closedCount ?? 0) || 0;

    return item;
  }
}

export class RecruitmentAverageOpenTimeByAreaItem {
  public area: string = '';
  public averageDays: number = 0;

  static create(input: unknown): RecruitmentAverageOpenTimeByAreaItem {
    const item = new RecruitmentAverageOpenTimeByAreaItem();
    const payload = isRecord(input) ? input : {};

    item.area = String(payload.area ?? '');
    item.averageDays = Number(payload.averageDays ?? 0) || 0;

    return item;
  }
}

export class RecruitmentIndicationByJobTitleGroupItem {
  public jobTitleGroupId: string = '';
  public jobTitleGroupName: string = '';
  public indicationCount: number = 0;
  public hiredCount: number = 0;
  public conversionRate: number = 0;

  static create(input: unknown): RecruitmentIndicationByJobTitleGroupItem {
    const item = new RecruitmentIndicationByJobTitleGroupItem();
    const payload = isRecord(input) ? input : {};

    item.jobTitleGroupId = String(payload.jobTitleGroupId ?? '');
    item.jobTitleGroupName = String(payload.jobTitleGroupName ?? '');
    item.indicationCount = Number(payload.indicationCount ?? 0) || 0;
    item.hiredCount = Number(payload.hiredCount ?? 0) || 0;
    item.conversionRate = Number(payload.conversionRate ?? 0) || 0;

    return item;
  }
}

export class RecruitmentAttractionByJobTitleGroupItem {
  public jobTitleGroupId: string = '';
  public jobTitleGroupName: string = '';
  public openedCount: number = 0;
  public candidateCount: number = 0;

  static create(input: unknown): RecruitmentAttractionByJobTitleGroupItem {
    const item = new RecruitmentAttractionByJobTitleGroupItem();
    const payload = isRecord(input) ? input : {};

    item.jobTitleGroupId = String(payload.jobTitleGroupId ?? '');
    item.jobTitleGroupName = String(payload.jobTitleGroupName ?? '');
    item.openedCount = Number(payload.openedCount ?? 0) || 0;
    item.candidateCount = Number(payload.candidateCount ?? 0) || 0;

    return item;
  }
}

export class RecruitmentAverageTimePerStageItem {
  public stageId: string = '';
  public stageName: string = '';
  public stageOrder: number = 0;
  public averageDays: number = 0;

  static create(input: unknown): RecruitmentAverageTimePerStageItem {
    const item = new RecruitmentAverageTimePerStageItem();
    const payload = isRecord(input) ? input : {};

    item.stageId = String(payload.stageId ?? '');
    item.stageName = String(payload.stageName ?? '');
    item.stageOrder = Number(payload.stageOrder ?? 0) || 0;
    item.averageDays = Number(payload.averageDays ?? 0) || 0;

    return item;
  }
}

export class RecruitmentByPurposeItem {
  public purpose: number | null = null;
  public purposeName: string = '';
  public count: number = 0;

  static create(input: unknown): RecruitmentByPurposeItem {
    const item = new RecruitmentByPurposeItem();
    const payload = isRecord(input) ? input : {};

    const purposeValue = payload.purpose;
    if (purposeValue == null || purposeValue === '') {
      item.purpose = null;
    } else {
      const parsedPurpose = Number(purposeValue);
      item.purpose = Number.isFinite(parsedPurpose) ? parsedPurpose : null;
    }
    item.purposeName = String(payload.purposeName ?? '');
    item.count = Number(payload.count ?? 0) || 0;

    return item;
  }
}

export interface ReasonByStageItem {
  stageId: string;
  stageName: string;
  stageOrder: number;
  reasonId: string | null;
  reasonName: string;
  count: number;
}

export class RecruitmentRejectionReasonByStageItem implements ReasonByStageItem {
  public stageId: string = '';
  public stageName: string = '';
  public stageOrder: number = 0;
  public reasonId: string | null = null;
  public reasonName: string = '';
  public count: number = 0;

  static create(input: unknown): RecruitmentRejectionReasonByStageItem {
    const item = new RecruitmentRejectionReasonByStageItem();
    const payload = isRecord(input) ? input : {};

    item.stageId = String(payload.stageId ?? '');
    item.stageName = String(payload.stageName ?? '');
    item.stageOrder = Number(payload.stageOrder ?? 0) || 0;
    item.reasonId = payload.reasonId == null ? null : String(payload.reasonId);
    item.reasonName = String(payload.reasonName ?? '');
    item.count = Number(payload.count ?? 0) || 0;

    return item;
  }
}

export class RecruitmentWithdrawalReasonByStageItem implements ReasonByStageItem {
  public stageId: string = '';
  public stageName: string = '';
  public stageOrder: number = 0;
  public reasonId: string | null = null;
  public reasonName: string = '';
  public count: number = 0;

  static create(input: unknown): RecruitmentWithdrawalReasonByStageItem {
    const item = new RecruitmentWithdrawalReasonByStageItem();
    const payload = isRecord(input) ? input : {};

    item.stageId = String(payload.stageId ?? '');
    item.stageName = String(payload.stageName ?? '');
    item.stageOrder = Number(payload.stageOrder ?? 0) || 0;
    item.reasonId = payload.reasonId == null ? null : String(payload.reasonId);
    item.reasonName = String(payload.reasonName ?? '');
    item.count = Number(payload.count ?? 0) || 0;

    return item;
  }
}

export class RecruitmentMetricsModel {
  public recruitmentByCostCenter: RecruitmentByCostCenterItem[] = [];
  public recruitmentByJobTitleGroup: RecruitmentByJobTitleGroupItem[] = [];
  public recruitmentByMonth: RecruitmentByMonthItem[] = [];
  public averageOpenTimeByArea: RecruitmentAverageOpenTimeByAreaItem[] = [];
  public indicationByJobTitleGroup: RecruitmentIndicationByJobTitleGroupItem[] = [];
  public attractionByJobTitleGroup: RecruitmentAttractionByJobTitleGroupItem[] = [];
  public averageTimePerStage: RecruitmentAverageTimePerStageItem[] = [];
  public recruitmentByPurpose: RecruitmentByPurposeItem[] = [];
  public rejectionReasonByStage: RecruitmentRejectionReasonByStageItem[] = [];
  public withdrawalReasonByStage: RecruitmentWithdrawalReasonByStageItem[] = [];

  static create(input: unknown): RecruitmentMetricsModel {
    const metrics = new RecruitmentMetricsModel();
    const payload = unwrapPayload(input);
    const costCenterItems = payload.recruitmentByCostCenter;
    const jobTitleGroupItems = payload.recruitmentByJobTitleGroup;
    const monthItems = payload.recruitmentByMonth;
    const averageOpenTimeByAreaItems = payload.averageOpenTimeByArea;
    const indicationItems = payload.indicationByJobTitleGroup;
    const attractionItems = payload.attractionByJobTitleGroup;
    const averageTimePerStageItems = payload.averageTimePerStage;
    const recruitmentByPurposeItems = payload.recruitmentByPurpose;
    const rejectionReasonByStageItems = payload.rejectionReasonByStage;
    const withdrawalReasonByStageItems = payload.withdrawalReasonByStage;

    metrics.recruitmentByCostCenter = Array.isArray(costCenterItems)
      ? costCenterItems.map(item => RecruitmentByCostCenterItem.create(item))
      : [];

    metrics.recruitmentByJobTitleGroup = Array.isArray(jobTitleGroupItems)
      ? jobTitleGroupItems.map(item => RecruitmentByJobTitleGroupItem.create(item))
      : [];

    metrics.recruitmentByMonth = Array.isArray(monthItems)
      ? monthItems
          .map(item => RecruitmentByMonthItem.create(item))
          .filter(item => isValidRecruitmentMonth(item))
      : [];

    metrics.averageOpenTimeByArea = Array.isArray(averageOpenTimeByAreaItems)
      ? averageOpenTimeByAreaItems.map(item => RecruitmentAverageOpenTimeByAreaItem.create(item))
      : [];

    metrics.indicationByJobTitleGroup = Array.isArray(indicationItems)
      ? indicationItems.map(item => RecruitmentIndicationByJobTitleGroupItem.create(item))
      : [];

    metrics.attractionByJobTitleGroup = Array.isArray(attractionItems)
      ? attractionItems.map(item => RecruitmentAttractionByJobTitleGroupItem.create(item))
      : [];

    metrics.averageTimePerStage = Array.isArray(averageTimePerStageItems)
      ? averageTimePerStageItems.map(item => RecruitmentAverageTimePerStageItem.create(item))
      : [];

    metrics.recruitmentByPurpose = Array.isArray(recruitmentByPurposeItems)
      ? recruitmentByPurposeItems.map(item => RecruitmentByPurposeItem.create(item))
      : [];

    metrics.rejectionReasonByStage = Array.isArray(rejectionReasonByStageItems)
      ? rejectionReasonByStageItems.map(item => RecruitmentRejectionReasonByStageItem.create(item))
      : [];

    metrics.withdrawalReasonByStage = Array.isArray(withdrawalReasonByStageItems)
      ? withdrawalReasonByStageItems.map(item => RecruitmentWithdrawalReasonByStageItem.create(item))
      : [];

    return metrics;
  }
}

function unwrapPayload(input: unknown): Record<string, unknown> {
  if (!isRecord(input)) {
    return {};
  }

  if (isRecord(input.data)) {
    return input.data;
  }

  if (isRecord(input.value)) {
    return input.value;
  }

  return input;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isValidRecruitmentMonth(item: RecruitmentByMonthItem): boolean {
  return item.year > 0 && item.month >= 1 && item.month <= 12;
}
